import React from 'react';
import { motion } from 'framer-motion';
import { CreditCardIcon, FileTextIcon, LockIcon, MapPinIcon, ShieldCheckIcon, UtensilsIcon, ZapIcon } from 'lucide-react';
import { CtaButton } from '../CtaButton';
import { ColombiaFlag } from '../ColombiaFlag';
import { Highlight } from '../Highlight';
import { heroBadges, heroStats, productImage, trustRow } from '../../data/landing';
const iconMap: Record<string, React.ReactNode> = {
  file: <FileTextIcon className="h-4 w-4 text-brand" data-id="element-134" data-mp="mrxzs7jp00qf" />,
  zap: <ZapIcon className="h-4 w-4 text-brand" data-id="element-135" data-mp="quualc2zqsjt" />,
  flag: <MapPinIcon className="h-4 w-4 text-brand" data-id="element-136" data-mp="qtjabm8r82xg" />,
  utensils: <UtensilsIcon className="h-4 w-4 text-brand" data-id="element-137" data-mp="vndrahwz63su" />,
  shield: <ShieldCheckIcon className="h-4 w-4 text-brand" data-id="element-138" data-mp="10lfg7jh30xc8" />,
  lock: <LockIcon className="h-4 w-4 text-brand" data-id="element-139" data-mp="10kxfoandzwyi" />,
  card: <CreditCardIcon className="h-4 w-4 text-brand" data-id="element-140" data-mp="15l1w60oaoroq" />
};
export function Hero() {
  return <header className="relative overflow-hidden bg-white px-5 pb-16 pt-12 md:pb-24 md:pt-16" data-id="element-141" data-mp="1ajd4u82usw5d">
      <div className="mx-auto flex max-w-5xl flex-col items-center text-center" data-id="element-142" data-mp="9nj8s73026a3">
        <p className="flex items-center gap-3 text-base font-extrabold uppercase tracking-[0.2em] text-brand md:text-lg" data-id="element-143" data-mp="9lg9sxjvd2t8">
          <ColombiaFlag className="h-4 w-6" data-id="element-144" data-mp="3vurbt2qg612y" />
          Hábil Online™
        </p>

        <h1 className="mt-6 text-5xl font-black leading-[1] tracking-tight text-brand sm:text-6xl md:text-8xl" data-id="element-145" data-mp="3rtffyfgzcjmb">
          Colon Tranquilo
          <span className="mt-2 block" data-id="element-146" data-mp="sd918njaifyu">
            <Highlight data-id="element-147" data-mp="sgeqc2rx5huk">en 7 Días.</Highlight>
          </span>
        </h1>

        <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} className="mx-auto mt-8 w-full max-w-[380px] sm:max-w-[500px] md:mt-10 md:max-w-[640px]" data-id="element-148" data-mp="ngipudifk8jr">
          <img src={productImage} width={1200} height={1000} alt="MegaPack Colon Tranquilo: 5 tomos, 5 bonos y Plan Maestro de 30 días" className="block h-auto w-full object-contain" data-id="element-149" data-mp="1b5to9ocb5quq" />
        </motion.div>

        <p className="mt-8 max-w-3xl text-xl font-extrabold leading-snug text-brand md:text-2xl" data-id="element-150" data-mp="35ljr8kiujt61">
          El sistema N°1 para personas con colon irritable en Colombia — Días 1 → 30 — Curación
          definitiva
        </p>
        <p className="mt-3 max-w-2xl text-base text-charcoal md:text-lg" data-id="element-151" data-mp="2vtw58z5274by">
          Menús + recetas + protocolos listos para usar. Sin buscar. Sin improvisar. Sin volver a
          sufrir.
        </p>

        <ul className="mt-6 flex flex-wrap justify-center gap-2" data-id="element-152" data-mp="2mvaisswgv55w">
          {heroBadges.map(b => <li key={b.label} className="flex items-center gap-2 rounded-full border border-line bg-brand-soft px-4 py-2 text-sm font-semibold text-charcoal" data-id="element-153" data-mp="27yt4ptww7cfn">
              {iconMap[b.icon]}
              {b.label}
            </li>)}
        </ul>

        <div className="mt-8 flex w-full justify-center" data-id="element-154" data-mp="b4g8zon2mnex">
          <CtaButton href="#oferta" data-id="element-155" data-mp="1vmhaqi1l7emj">Quiero mi MegaPack ahora</CtaButton>
        </div>

        <ul className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm font-medium text-muted" data-id="element-156" data-mp="1qnxla7nsbkpf">
          {trustRow.map(t => <li key={t.label} className="flex items-center gap-2" data-id="element-157" data-mp="25dlr9m6wv7jh">
              {iconMap[t.icon]}
              {t.label}
            </li>)}
        </ul>

        <dl className="mt-12 grid w-full max-w-4xl grid-cols-2 overflow-hidden rounded-2xl bg-brand md:grid-cols-4" data-id="element-158" data-mp="2ed8k3jlkr50x">
          {heroStats.map((s, i) => <div key={s.label} className={`px-4 py-6 ${i < 2 ? 'border-b border-white/15 md:border-b-0' : ''} ${i % 2 === 0 ? 'border-r border-white/15' : ''} ${i === 1 ? 'md:border-r' : ''}`} data-id="element-159" data-mp="2abnac74c76uy" data-mpi={i}>
              <dt className="sr-only" data-id="element-160" data-mp="tyq050r2vdm1">{s.label}</dt>
              <dd className="text-3xl font-black text-gold md:text-4xl" data-id="element-161" data-mp="tzhuy9cup7b9">{s.value}</dd>
              <dd className="mt-1 text-sm font-medium text-white/85" data-id="element-162" data-mp="p0okyqwrvyfu">{s.label}</dd>
            </div>)}
        </dl>
      </div>
    </header>;
}